import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { API, adminLogo } from "@/lib/api";
import { useEffect, useState } from "react";
import {
  LayoutDashboard, ShoppingBag, Package, Boxes, Wallet, FileText, Users, BarChart3, MessageSquare, Settings, LogOut, Menu, X, Globe,
  BookOpen, Truck, TicketPercent, ClipboardCheck, Palette, Newspaper, Film, Images,
} from "lucide-react";

const MENU = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/pesanan", label: "Pesanan", icon: ShoppingBag },
  { to: "/admin/produk", label: "Produk", icon: Package },
  { to: "/admin/stok", label: "Persediaan", icon: Boxes },
  { to: "/admin/keuangan", label: "Keuangan", icon: Wallet },
  { to: "/admin/akunting", label: "Kas & Akunting", icon: BookOpen },
  { to: "/admin/invoice", label: "Invoice", icon: FileText },
  { to: "/admin/surat-jalan", label: "Surat Jalan", icon: Truck },
  { to: "/admin/tanda-terima", label: "Tanda Terima Barang", icon: ClipboardCheck },
  { to: "/admin/voucher", label: "Voucher Diskon", icon: TicketPercent },
  { to: "/admin/crm", label: "CRM", icon: Users },
  { to: "/admin/laporan", label: "Laporan", icon: BarChart3 },
  { to: "/admin/konten", label: "Testimoni & Pesan", icon: MessageSquare },
  { to: "/admin/website", label: "Kelola Website", icon: Palette },
  { to: "/admin/blog", label: "Kelola Blog", icon: Newspaper },
  { to: "/admin/video", label: "Kelola Video", icon: Film },
  { to: "/admin/galeri", label: "Kelola Galeri", icon: Images },
  { to: "/admin/pengaturan", label: "Pengaturan", icon: Settings },
];

function AdminBrand({ site, compact = false }) {
  return (
    <div className="flex min-w-0 flex-1 items-center gap-2.5">
      <img
        src={adminLogo(site)}
        alt="Logo NAWADAH"
        className={`${compact ? "h-9 w-9" : "h-10 w-10"} shrink-0 rounded-full border border-slate-200 bg-white object-contain`}
      />
      <div className="min-w-0">
        <p className={`${compact ? "text-[13px] sm:text-sm" : "text-[13px]"} font-display font-bold leading-tight text-[#0B1F3A]`}>
          NAWADAH by NDH Official
        </p>
        <p className="mt-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] text-[#C9A227]">
          Sistem Bisnis
        </p>
      </div>
    </div>
  );
}

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [site, setSite] = useState({});

  useEffect(() => {
    if (user === false) navigate("/masuk?next=/admin");
    else if (user && user.role !== "owner") navigate("/toko");
  }, [user, navigate]);

  useEffect(() => {
    const loadSettings = () => API.get("/settings").then((r) => setSite(r.data || {})).catch(() => {});
    loadSettings();
    window.addEventListener("nawadah:settings-updated", loadSettings);
    return () => window.removeEventListener("nawadah:settings-updated", loadSettings);
  }, []);

  if (!user || user.role !== "owner") {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 bg-slate-100">Memuat...</div>;
  }

  const sidebar = (
    <div className="flex h-full min-h-0 flex-col">
      <Link to="/admin" onClick={() => setOpen(false)} className="flex min-h-20 min-w-0 shrink-0 items-center border-b border-slate-200 px-4 py-3">
        <AdminBrand site={site} />
      </Link>
      <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto overscroll-contain px-3 py-4" data-testid="admin-nav">
        {MENU.map((m) => (
          <NavLink
            key={m.to}
            to={m.to}
            end={m.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-nav-${m.label.toLowerCase().replace(/\s|&/g, "-")}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 rounded-md text-sm font-medium transition-colors ${
                isActive ? "bg-[#0B1F3A] text-white" : "text-slate-600 hover:bg-slate-100"
              }`
            }
          >
            <m.icon className="h-4 w-4" /> {m.label}
          </NavLink>
        ))}
      </nav>
      <div className="shrink-0 space-y-1 border-t border-slate-200 bg-white p-3">
        <Link to="/" data-testid="admin-view-site" className="flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">
          <Globe className="h-4 w-4" /> Lihat Website
        </Link>
        <button
          onClick={async () => { await logout(); navigate("/"); }}
          data-testid="admin-logout"
          className="w-full flex items-center gap-3 px-4 py-2.5 rounded-md text-sm text-red-600 hover:bg-red-50 transition-colors"
        >
          <LogOut className="h-4 w-4" /> Keluar
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 flex">
      <aside className="fixed inset-y-0 z-40 hidden w-64 border-r border-slate-200 bg-white print:hidden lg:block">{sidebar}</aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-white">{sidebar}</aside>
        </div>
      )}
      <div className="min-w-0 flex-1 print:ml-0 lg:ml-64">
        <header className="sticky top-0 z-30 flex h-16 items-center gap-2 border-b border-slate-200 bg-white px-3 lg:hidden">
          <button onClick={() => setOpen(true)} data-testid="admin-mobile-menu" aria-label="Buka menu admin" className="shrink-0 p-2"><Menu className="h-5 w-5" /></button>
          <AdminBrand site={site} compact />
        </header>
        <main className="min-w-0 p-4 print:p-0 sm:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
